<template>
    <GeneratingProgressBar
        v-if="isGenerating"
        message="Generating your BPMN, this may take a minute..."
    />
    <Fluid v-else class="field">
        <div class="label">
            <label>Describe your process</label>
            <small :class="{ 'error': tooLong }">
                {{ description.length }}/{{ PROMPT_MAX_LENGTH }}
            </small>
        </div>
        <Message v-if="tooLong" small severity="warn">
            Max description length reached.
        </Message>
        <Textarea
            id="description"
            autofocus
            v-model="description"
            required
            rows="12"
        />
        <small>
            Roughly describe what you want to model. Don't be too precise, you
            will be able to modify the result.
        </small>
    </Fluid>
    <div class="buttons" v-if="!isGenerating">
        <Button label="Cancel" text severity="secondary" @click="cancel" />
        <Button
            label="Generate"
            severity="primary"
            v-tooltip.top="tooLong && 'Description is too long'"
            :disabled="tooLong"
            @click="generate"
        />
    </div>
</template>

<script setup lang="ts">
import { Ref, computed, inject, ref } from "vue";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Textarea from "primevue/textarea";
import { useHotKeys } from "@/composables/hotkeys";
import { PROMPT_MAX_LENGTH } from "@/composables/process-manager";
import GeneratingProgressBar from "../GeneratingProgressBar.vue";

const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");
const isGenerating = computed(() => {
    return dialogRef?.value.data.getIsGenerating?.();
});

const tooLong = computed(() => {
    return description.value.length >= PROMPT_MAX_LENGTH;
});

const description = ref("");

const { addHotKey } = useHotKeys();

const cancel = () => dialogRef?.value.close();

const generate = async () => {
    if (!description.value) {
        return;
    }
    await dialogRef?.value.data.generateCallback(description.value);
    dialogRef?.value.close();
};

/**
 * Save on cmd/ctrl + enter.
 */
addHotKey("ControlOrMeta+Enter", async () => {
    await generate();
});
</script>

<style lang="scss" scoped>
.field {
    padding-bottom: 1em;

    * {
        display: block;
        margin-bottom: 0.5em;
    }
}

.error {
    color: red;
}

.label {
    display: flex;

    small {
        flex: 0;
    }

    label {
        flex: 1;
    }
}

.buttons {
    display: flex;
    justify-content: end;
    margin-top: 1em;
    gap: 1em;
}
</style>
